import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi, ApiError, type SignupInput } from './api-client';
import type { Role } from './types';
import {
  clearCredentials,
  getAccessToken,
  getApiKey,
  getImpersonationOrigin,
  getStoredPrincipal,
  hasCredentials,
  setAccessToken,
  setApiKey,
  setImpersonationOrigin,
  setStoredPrincipal,
  subscribeToCredentials,
  type StoredPrincipal,
} from './token-store';

export type Principal = StoredPrincipal;

/** Set only while a BUI staff member is "switched in" to a customer workspace — the workspace
 * they're viewing, so `AppShell`'s banner (and anything else) can read it without reaching into
 * `token-store` directly. */
export interface ImpersonationState {
  workspaceName: string;
}

interface AuthContextValue {
  principal: Principal | null;
  isAuthenticated: boolean;
  login: (input: { tenantSlug: string; email: string; password: string }) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  loginWithApiKey: (key: string) => void;
  ssoLogin: (tenantSlug: string, email?: string) => Promise<void>;
  completeSsoLogin: (input: { code: string; state: string }) => Promise<void>;
  logout: () => void;
  /** Non-null exactly while viewing a workspace via BUI Super-Admin Console impersonation. */
  impersonating: ImpersonationState | null;
  /** Stashes the CURRENT (BUI staff) session, then switches the active session to the
   * impersonation token/principal returned by `POST /v1/admin/workspaces/:id/impersonate`. */
  impersonate: (input: { accessToken: string; tenantId: string; userId: string; role: Role; workspaceName: string }) => void;
  /** Restores the stashed BUI staff session and clears impersonation state. No-op if not
   * currently impersonating. */
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Exported so callers outside the SSO flow (e.g. `AccessDenied`'s "sign in with a
 * different account" action) can defensively clear a lingering SSO state token even
 * though `completeSsoLogin` already removes it as soon as it reads it.
 */
export const SSO_STATE_KEY = 'defendiq.sso_state';

/** A stored principal is only trusted alongside a live credential — avoids resurrecting a stale principal from a corrupted or partially-cleared session. */
function initialPrincipal(): Principal | null {
  return hasCredentials() ? getStoredPrincipal() : null;
}

/** An impersonation origin surviving in `sessionStorage` across a page reload means the "viewing
 * as" banner should come right back too — it's the one piece of impersonation state genuinely
 * worth persisting (unlike, say, exposing it as a resumable action), since losing the banner on
 * reload would leave a BUI staff member looking at a customer workspace with no indication
 * they're not in their own. */
function initialImpersonating(): ImpersonationState | null {
  const origin = getImpersonationOrigin();
  return origin ? { workspaceName: origin.workspaceName } : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(initialPrincipal);
  const [impersonating, setImpersonating] = useState<ImpersonationState | null>(initialImpersonating);

  useEffect(
    () =>
      subscribeToCredentials(() => {
        if (!hasCredentials()) setPrincipal(null);
      }),
    [],
  );

  /** Updates both the persisted session and this provider's React state together — every login path must keep them in sync. */
  const applyPrincipal = useCallback((next: Principal | null) => {
    setStoredPrincipal(next);
    setPrincipal(next);
  }, []);

  const login = useCallback(
    async (input: { tenantSlug: string; email: string; password: string }) => {
      const result = await authApi.login(input);
      setAccessToken(result.accessToken);
      applyPrincipal({ tenantId: result.tenantId, userId: result.userId, role: result.role });
    },
    [applyPrincipal],
  );

  const signup = useCallback(
    async (input: SignupInput) => {
      const result = await authApi.signup(input);
      setAccessToken(result.accessToken);
      applyPrincipal({ tenantId: result.tenant.id, userId: result.user.id, role: result.role });
    },
    [applyPrincipal],
  );

  const loginWithApiKey = useCallback(
    (key: string) => {
      setApiKey(key);
      applyPrincipal({ tenantId: '', userId: '', role: undefined });
    },
    [applyPrincipal],
  );

  const ssoLogin = useCallback(async (tenantSlug: string, email?: string) => {
    const { url, ssoState } = await authApi.ssoLogin({ tenantSlug, ...(email ? { email } : {}) });
    window.sessionStorage.setItem(SSO_STATE_KEY, ssoState);
    window.location.assign(url);
  }, []);

  const completeSsoLogin = useCallback(
    async (input: { code: string; state: string }) => {
      const ssoState = window.sessionStorage.getItem(SSO_STATE_KEY);
      if (!ssoState) {
        throw new ApiError(400, 'SSO_STATE_MISSING', 'No in-progress SSO login found in this browser session.');
      }
      window.sessionStorage.removeItem(SSO_STATE_KEY);
      const result = await authApi.ssoCallback({ code: input.code, state: input.state, ssoState });
      setAccessToken(result.accessToken);
      applyPrincipal({ tenantId: result.tenantId, userId: result.userId, role: result.role });
    },
    [applyPrincipal],
  );

  const logout = useCallback(() => {
    clearCredentials();
    setPrincipal(null);
    setImpersonating(null);
  }, []);

  const impersonate = useCallback(
    (input: { accessToken: string; tenantId: string; userId: string; role: Role; workspaceName: string }) => {
      const existingOrigin = getImpersonationOrigin();
      if (existingOrigin) {
        // Already impersonating something else (switched straight from workspace A to B without
        // exiting first) — keep the REAL BUI staff credential/principal untouched (there's only
        // one "way back," and it must always be BUI), just update which workspace a reload
        // should restore the banner for.
        setImpersonationOrigin({ ...existingOrigin, workspaceName: input.workspaceName });
      } else if (principal) {
        setImpersonationOrigin({ accessToken: getAccessToken(), apiKey: getApiKey(), principal, workspaceName: input.workspaceName });
      }
      setAccessToken(input.accessToken);
      applyPrincipal({ tenantId: input.tenantId, userId: input.userId, role: input.role });
      setImpersonating({ workspaceName: input.workspaceName });
    },
    [principal, applyPrincipal],
  );

  const exitImpersonation = useCallback(() => {
    const origin = getImpersonationOrigin();
    if (!origin) return;
    if (origin.apiKey) setApiKey(origin.apiKey);
    else if (origin.accessToken) setAccessToken(origin.accessToken);
    applyPrincipal(origin.principal);
    setImpersonationOrigin(null);
    setImpersonating(null);
  }, [applyPrincipal]);

  const value = useMemo<AuthContextValue>(
    () => ({
      principal,
      isAuthenticated: principal !== null,
      login,
      signup,
      loginWithApiKey,
      ssoLogin,
      completeSsoLogin,
      logout,
      impersonating,
      impersonate,
      exitImpersonation,
    }),
    [principal, login, signup, loginWithApiKey, ssoLogin, completeSsoLogin, logout, impersonating, impersonate, exitImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
